import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { employeesApi, type EmployeeSummaryDto } from "@/lib/employees";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";

export interface EmployeePickerProps {
  value: number | null;
  valueLabel?: string | null;
  onChange: (id: number | null, employee: EmployeeSummaryDto | null) => void;
  placeholder?: string;
}

export function EmployeePicker({
  value,
  valueLabel,
  onChange,
  placeholder,
}: EmployeePickerProps) {
  const [q, setQ] = useState(valueLabel ?? "");

  // Sync internal search query if the external label changes
  useEffect(() => {
    if (valueLabel != null) {
      setQ(valueLabel);
    }
  }, [valueLabel]);

  const { data } = useQuery({
    queryKey: ["employee-finder", q],
    queryFn: () => employeesApi.list({ q, pageSize: 10 }),
    select: (r) => r.items,
    enabled: q.length >= 2,
    staleTime: 1500,
  });

  return (
    // shouldFilter={false}: server-side search via useQuery; cmdk's built-in
    // client-side filter would hide items that don't match current input before
    // async results arrive.
    <Command shouldFilter={false} className="h-auto border border-border rounded-md">
      <CommandInput
        placeholder={placeholder ?? "Search employees..."}
        value={q}
        onValueChange={(newQ) => {
          setQ(newQ);
          // If the user starts typing and changes the selected text, clear the selection
          if (value != null && newQ !== valueLabel) {
            onChange(null, null);
          }
        }}
      />
      {q.length > 0 && value == null && (
        <CommandList>
          {data && data.length === 0 && <CommandEmpty>No matches</CommandEmpty>}
          {data?.map((e) => (
            <CommandItem
              key={e.id}
              value={String(e.id)}
              onSelect={() => {
                setQ(e.fullName);
                onChange(e.id, e);
              }}
            >
              <span>{e.fullName}</span>
              {e.email && (
                <span className="ml-2 text-muted-foreground">{e.email}</span>
              )}
            </CommandItem>
          ))}
        </CommandList>
      )}
    </Command>
  );
}
